import { useMutation, useQuery } from "convex/react";
import {
  CheckCircle,
  Plug,
  WhatsappLogo,
  XCircle,
} from "@phosphor-icons/react";
import { useId, useState } from "react";

import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type { CurrentMembership } from "./types";

type Feedback = { tone: "good" | "bad" | "neutral"; message: string };

function ownerCanConfigure(currentMembership: CurrentMembership | null) {
  return currentMembership?.role === "OWNER";
}

function connectionStatusLabel(status: string | undefined) {
  if (!status) return "Checking";
  return status.replaceAll("_", " ").toLowerCase();
}

function normalizeError(message: string) {
  if (message.includes("INSUFFICIENT_ROLE")) {
    return "Only owners can connect a WhatsApp number.";
  }
  if (message.includes("TENANT_ACCESS_DENIED")) {
    return "Access was denied for this business.";
  }
  if (message.includes("WHATSAPP_SETUP_VERIFICATION_CODE_INVALID")) {
    return "The verification code does not match.";
  }

  return "WhatsApp setup could not be completed.";
}

export function WhatsAppConnectionPanel({
  businessId,
  currentMembership,
}: {
  businessId: Id<"businesses">;
  currentMembership: CurrentMembership | null;
}) {
  const health = useQuery(api.whatsAppConnectionSetup.getConnectionHealth, {
    businessId,
  });
  const canConfigure = ownerCanConfigure(currentMembership);

  return (
    <section className="rail-panel" aria-label="WhatsApp connection">
      <ConnectionHeading status={health?.status} />
      <ConnectionHealthSummary
        displayPhoneNumber={health?.connection?.displayPhoneNumber ?? null}
        status={health?.status}
      />
      {canConfigure ? (
        <ConnectionSetupForm businessId={businessId} />
      ) : (
        <span className="hint">
          Owner access is required to connect a number.
        </span>
      )}
    </section>
  );
}

function ConnectionHeading({ status }: { status: string | undefined }) {
  return (
    <div className="panel-heading">
      <span>
        <WhatsappLogo weight="bold" />
        WhatsApp connection
      </span>
      {status === "CONNECTED" ? (
        <CheckCircle weight="bold" />
      ) : (
        <Plug weight="bold" />
      )}
    </div>
  );
}

function ConnectionHealthSummary({
  displayPhoneNumber,
  status,
}: {
  displayPhoneNumber: string | null;
  status: string | undefined;
}) {
  return (
    <p className="panel-empty">
      {connectionStatusLabel(status)}
      {displayPhoneNumber ? `: ${displayPhoneNumber}` : ""}
    </p>
  );
}

function ConnectionSetupForm({ businessId }: { businessId: Id<"businesses"> }) {
  const startSetup = useMutation(api.whatsAppConnectionSetup.startSetup);
  const submitPhoneNumber = useMutation(
    api.whatsAppConnectionSetup.submitPhoneNumber,
  );
  const confirmVerification = useMutation(
    api.whatsAppConnectionSetup.confirmVerification,
  );
  const connectVerifiedNumber = useMutation(
    api.whatsAppConnectionSetup.connectVerifiedNumber,
  );
  const [displayPhoneNumber, setDisplayPhoneNumber] = useState("");
  const [kapsoPhoneNumberId, setKapsoPhoneNumberId] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [setupSessionId, setSetupSessionId] =
    useState<Id<"whatsAppConnectionSetupSessions"> | null>(null);
  const [pending, setPending] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const clientRequestId = `nk39-ui-${useId().replaceAll(":", "")}`;

  async function requestVerification() {
    if (pending) return;

    setPending(true);
    setFeedback(null);
    try {
      const started =
        setupSessionId ??
        (
          await startSetup({
            businessId,
            clientRequestId,
          })
        ).setupSessionId;
      const submitted = await submitPhoneNumber({
        businessId,
        setupSessionId: started,
        displayPhoneNumber,
        kapsoPhoneNumberId,
      });

      setSetupSessionId(started);
      setVerificationCode(submitted.verificationCode);
      setFeedback({
        tone: "neutral",
        message: "Verification code generated for this setup session.",
      });
    } catch (error) {
      setFeedback({ tone: "bad", message: normalizeError(String(error)) });
    } finally {
      setPending(false);
    }
  }

  async function connectNumber() {
    if (!setupSessionId || pending) return;

    setPending(true);
    setFeedback(null);
    try {
      await confirmVerification({
        businessId,
        setupSessionId,
        verificationCode,
      });
      await connectVerifiedNumber({ businessId, setupSessionId });
      setFeedback({ tone: "good", message: "WhatsApp connection is active." });
    } catch (error) {
      setFeedback({ tone: "bad", message: normalizeError(String(error)) });
    } finally {
      setPending(false);
    }
  }

  const verifyDisabled = !displayPhoneNumber || !kapsoPhoneNumberId || pending;
  const connectDisabled = !setupSessionId || !verificationCode || pending;

  return (
    <div className="composer compact-composer">
      <input
        aria-label="WhatsApp display phone number"
        onChange={(event) => setDisplayPhoneNumber(event.target.value)}
        placeholder="+27821234567"
        value={displayPhoneNumber}
      />
      <input
        aria-label="Kapso phone number ID"
        onChange={(event) => setKapsoPhoneNumberId(event.target.value)}
        placeholder="Kapso phone number ID"
        value={kapsoPhoneNumberId}
      />
      <button
        disabled={verifyDisabled}
        onClick={requestVerification}
        type="button"
      >
        {pending ? "Checking" : "Verify"}
      </button>
      <input
        aria-label="Verification code"
        onChange={(event) =>
          setVerificationCode(event.target.value.toUpperCase())
        }
        placeholder="Code"
        value={verificationCode}
      />
      <button disabled={connectDisabled} onClick={connectNumber} type="button">
        Connect
      </button>
      <FeedbackMessage feedback={feedback} />
    </div>
  );
}

function FeedbackMessage({ feedback }: { feedback: Feedback | null }) {
  if (!feedback) return null;

  return (
    <span className={`feedback ${feedback.tone}`} role="status">
      {feedback.tone === "bad" ? <XCircle weight="bold" /> : null}
      {feedback.message}
    </span>
  );
}
